import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { BubbleChart } from "./bubble-chart";
import { Filters } from "./filters";
import { StatsCard } from "./stats-card";
import { WordCloudDisplay } from "./word-cloud-display";

interface WordCloudPageProps {
  data:
    | {
        words: Array<{ text: string; value: number }>;
        metadata: {
          totalMessages: number;
          uniqueWords: number;
          dateRange: { start: string | null; end: string | null };
          filters: {
            mentorId?: string;
            menteeId?: string;
            source: string;
          };
        };
      }
    | undefined;
  isPending: boolean;
  mentorId: string;
  menteeId: string;
  source: string;
  limit: number;
  startDate?: Date;
  endDate?: Date;
  viewMode: "bubble" | "cloud";
  handleFilterChange: (name: string, value: string) => void;
  handleDateChange: (type: "start" | "end", date: Date | undefined) => void;
  handleViewChange: (view: "bubble" | "cloud") => void;
}

export function WordCloudPage({
  data,
  isPending,
  mentorId,
  menteeId,
  source,
  limit,
  startDate,
  endDate,
  viewMode,
  handleFilterChange,
  handleDateChange,
  handleViewChange,
}: WordCloudPageProps) {
  return (
    <div className="m-6 space-y-6 pb-16">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold leading-7 text-black">
            Message Word Analysis
          </h2>
          <p className="text-sm text-gray-600">
            Explore the most commonly used words in messages
          </p>
        </div>

        {/* View Toggle */}
        <div className="flex gap-2">
          <Badge
            variant={viewMode === "bubble" ? "secondary" : "outline"}
            className={cn("cursor-pointer px-4 py-2", {
              "bg-black text-white hover:bg-black": viewMode === "bubble",
            })}
            onClick={() => handleViewChange("bubble")}
          >
            Bubble Chart
          </Badge>
          <Badge
            variant={viewMode === "cloud" ? "secondary" : "outline"}
            className={cn("cursor-pointer px-4 py-2", {
              "bg-black text-white hover:bg-black": viewMode === "cloud",
            })}
            onClick={() => handleViewChange("cloud")}
          >
            Word Cloud
          </Badge>
        </div>
      </div>

      <StatsCard
        totalMessages={data?.metadata.totalMessages || 0}
        uniqueWords={data?.metadata.uniqueWords || 0}
        dateRange={data?.metadata.dateRange || { start: null, end: null }}
        isLoading={isPending}
      />

      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-1">
          <Filters
            mentorId={mentorId}
            menteeId={menteeId}
            source={source}
            limit={limit}
            startDate={startDate}
            endDate={endDate}
            handleFilterChange={handleFilterChange}
            handleDateChange={handleDateChange}
          />
        </div>

        <div className="col-span-2">
          {viewMode === "bubble" ? (
            <BubbleChart words={data?.words || []} isLoading={isPending} />
          ) : (
            <WordCloudDisplay words={data?.words || []} isLoading={isPending} />
          )}
        </div>
      </div>
    </div>
  );
}
